import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { ErrorBanner } from '@/components/common/ErrorBanner';
import { BackButton } from '@/components/common/BackButton';
import UploadImage from '@/components/common/UploadImage';

import { useCreateCommunityPartnerMutation } from '@/store/api/partnersApi';

// ---------------- SCHEMA ----------------
const communityPartnerSchema = z.object({
  name: z.string().min(2, 'Partner name is required').max(80, 'Too long'),

  logo: z.string().min(1, 'Logo is required'),
});

type CommunityPartnerFormData = z.infer<typeof communityPartnerSchema>;

// ---------------- COMPONENT ----------------
const CreateCommunityPartner = () => {
  const [isImageUploading, setIsImageUploading] = useState(false);
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<CommunityPartnerFormData>({
    resolver: zodResolver(communityPartnerSchema),
    defaultValues: {
      name: '',
      logo: '',
    },
  });

  const [createPartner, { isLoading, error }] = useCreateCommunityPartnerMutation();

  const onSubmit = async (data: CommunityPartnerFormData) => {
    try {
      await createPartner({
        name: data.name.trim(),
        logo: data.logo.split('/').pop() || '',
      }).unwrap();

      navigate('/community-partners');
    } catch (err) {
      console.error('Create Community Partner Failed:', err);
    }
  };

  return (
    <div className="space-y-8 xl:max-w-[80%]">
      <div className="flex items-center gap-5 mb-4">
        <BackButton />
        <h2 className="text-2xl font-bold">Add Community Partner</h2>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 max-w-xl">
        {error && (
          <ErrorBanner
            type="error"
            title="Error Creating Partner"
            message={(error as any)?.data?.message || 'Something went wrong'}
          />
        )}

        {/* Partner Title */}
        <div className="space-y-2">
          <Label>Partner Name</Label>
          <Input placeholder="Google, Microsoft, NGO name..." {...register('name')} />
          {errors.name && <p className="text-sm text-destructive">{errors.name.message}</p>}
        </div>

        {/* Logo Upload */}
        <div className="space-y-2">
          <UploadImage
            label="Partner Logo"
            onChange={(url) => {
              setValue('logo', url, { shouldValidate: true });
            }}
            setIsImageUploading={setIsImageUploading}
          />
          {errors.logo && <p className="text-sm text-destructive">{errors.logo.message}</p>}
        </div>

        <Button disabled={isLoading || isImageUploading} type="submit">
          {isLoading ? 'Creating...' : 'Create Partner'}
        </Button>
      </form>
    </div>
  );
};

export default CreateCommunityPartner;
